import { type OnChangeFn, type SortingState, useTable } from '@tanstack/react-table';

import type { AssetUserType } from '@/common/asset';
import { DataTable } from '@/components/data-table/data-table';
import type { BulkSelection } from '@/hooks/use-bulk-selection';
import { assetUserColumns, assetUserTableFeatures } from './columns';

export type AssetUserListProps = {
	assetUsers?: AssetUserType[] | null;
	onDeleteUser: (userId: string) => Promise<void>;
	/** Server-side sort state, owned by the container so it can go into the list query. */
	sorting?: SortingState;
	onSortingChange?: OnChangeFn<SortingState>;
	/** When given, each row gets a checkbox for bulk actions. */
	selection?: BulkSelection;
};

const NO_ASSET_USERS: AssetUserType[] = [];
const EMPTY_SORTING: SortingState = [];

export function AssetUserList({
	assetUsers,
	onDeleteUser,
	sorting = EMPTY_SORTING,
	onSortingChange,
	selection,
}: AssetUserListProps) {
	const table = useTable({
		features: assetUserTableFeatures,
		columns: assetUserColumns,
		data: assetUsers ?? NO_ASSET_USERS,
		getRowId: (row) => row.id as string,
		state: { sorting, columnVisibility: { select: !!selection } },
		onSortingChange,
		// The API sorts the list; sorting client-side would duplicate it.
		manualSorting: true,
		// Skip auto-detecting the first direction from row values; always start ascending.
		sortDescFirst: false,
		meta: {
			onDeleteUser: (assetUser) => onDeleteUser(assetUser.id as string),
			selection,
		},
	});

	// The container renders the "no users assigned" empty state.
	if (!assetUsers?.length) return null;

	return (
		<DataTable
			table={table}
			getRowClassName={(row) => selection?.isSelected(row.original.id as string) && 'bg-primary/5'}
		/>
	);
}
